import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { ManualCourse } from '@edtech-ui/core/models/manual-course/manual-couse-payload';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { UnpublishedCourseDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/create-course/unpublished-course-dialog-box/unpublished-course-dialog-box.component';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { ToastrService } from 'ngx-toastr';
import { catchError, filter, finalize, of, Subscription, tap } from 'rxjs';

@Component({
  selector: 'app-admin-course-preview',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonModule, MatIcon, HasPermissionDirective],
  templateUrl: './admin-course-preview.component.html',
  styleUrl: './admin-course-preview.component.scss'
})
export class AdminCoursePreviewComponent {
  classroomId: any = '';
  courseId: string = '';
  coursePayload: ManualCourse = {
    course_code: '',
    course_title: '',
    grade_level: '',
    credits: 0,
    course_description: '',
    course_duration: 0,
    internal_marks: 0,
    exam_marks: 0,
    total_units_count: 0,
    course_objectives: [],
    course_outcomes: [],
    units: [],
    textbooks: [],
    web_references_links: [],
    status: 'Draft'
  };
  isLoadingDraft = false;
  isLoadingPublish = false;
  private subscription: Subscription = new Subscription();
  isPublish: boolean = false;
  _syllabusData: SyllabusResponseAI['Data'] | null = null;
  status: CourseStatusEnum | null = null;
  classroomid!: number;
  courseStatus = CourseStatusEnum;
  PermissionForStudents = PermissionForStudents;


  constructor(
    private courseService: CourseManagementService,
    private _toast: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private _toastr: ToastrService,
    private dialog: MatDialog,

  ) { }

  ngOnInit(): void {
    this.route.parent?.paramMap.subscribe(params => {
      this.classroomId = params.get('classroomId') ?? '';
    });
    this.route.queryParamMap.subscribe(params => {
      const courseId = params.get('courseId');
      if (courseId) {
        this.courseId = courseId;
        this.loadCourseData();
      }
    });
    this.subscription.add(
      this.courseService.payloadChanges().subscribe(payload => {
        if (!this.courseId && payload) {
          this.coursePayload = payload;
        }
      })
    );
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      if (!e.url.includes('/admin-student-platform')) {
        this.courseService.clearPayload();
      }
    });
  }

  loadCourseData() {
    if (this.courseId) {
      this.subscription.add(
        this.courseService.getCoursesByCourseId(this.courseId)
          .pipe(
            tap((response: CompleteSyllabusResponseAI) => {
              if (response?.Success && response.Data) {
                this._syllabusData = response.Data;
                this.coursePayload = response.Data as any;
                this.status = response.Data.status as CourseStatusEnum;
              }
            }),
            catchError(err => {
              this._toastr.error('Error fetching course');
              return of(null);
            })
          )
          .subscribe()
      );
    } else {
      const payload = this.courseService.getPayload();
      if (payload) {
        this.coursePayload = payload;
      }
    }
  }

  saveAsDraft(): void {
    this.postManualCourse(this.coursePayload, CourseStatusEnum.Draft);
    // this.router.navigate(['/admin-student-platform/admin-courses']);

  }

  publishCourse(): void {
    this.postManualCourse(this.coursePayload, CourseStatusEnum.Published);
    // this.router.navigate(['/admin-student-platform/admin-courses']);
  }
  postManualCourse(payload: ManualCourse, status: CourseStatusEnum): void {
    status === CourseStatusEnum.Draft
      ? (this.isLoadingDraft = true)
      : (this.isLoadingPublish = true);

    const preparedPayload: ManualCourse = {
      ...payload,

      // credits: payload.credits > 0 ? payload.credits : 1,
      // course_duration: payload.course_duration > 0 ? payload.course_duration : 1,
      // internal_marks: payload.internal_marks > 0 ? payload.internal_marks : 1,
      // exam_marks: payload.exam_marks > 0 ? payload.exam_marks : 1,
      total_units_count: payload.units.length,

      course_description: payload.course_description || '',
      course_objectives: payload.course_objectives ?? [],
      course_outcomes: payload.course_outcomes ?? [],

      units: payload.units.map(unit => ({
        unit_id: unit.unit_id || '',
        unit_title: unit.unit_title,
        unit_hours: Number(unit.unit_hours),
        topics: unit.topics.map(topic => ({
          topic_id: topic.topic_id || '',
          topic_title: topic.topic_title,
          description: topic.description || '',
          subtopics: topic.subtopics.map(sub => ({
            subtopic_title: sub.subtopic_title,
            description: sub.description || '',
            reference_links: sub.reference_links ?? []
          }))
        }))
      })),

      textbooks: payload.textbooks.map(book => ({
        title: book.title,
        author: book.author,
        publisher: book.publisher,
        year: Number(book.year),
        isbn: book.isbn || ''
      })),

      web_references_links: payload.web_references_links ?? [],
      status: status === CourseStatusEnum.Published ? 'Published' : 'Draft',
      rating: payload.rating ?? 0,
    };

    this.courseService.saveCourse(preparedPayload).subscribe({
      next: (res: any) => {
        this.isLoadingDraft = false;
        this.isLoadingPublish = false;

        if (res?.Success) {
          this._toast.success(res.Message || 'Course saved successfully');
          this.courseService.clearPayload();

          this.router.navigate(['/admin-student-platform/admin-courses']);

        } else {
          this._toast.error(res?.Message || 'Failed to save course');
        }
      },
      error: () => {
        this.isLoadingDraft = false;
        this.isLoadingPublish = false;

        this._toast.error('Something went wrong while saving the course');
      }
    });
  }

  getCourseDetailsById() {
    if (this.courseId && this.classroomId) {
      this.subscription.add(
        this.courseService.getCoursesByCourseId(this.courseId).subscribe({
          next: (response: CompleteSyllabusResponseAI) => {
            if (response.Success && response.Data) {
              this._syllabusData = response.Data;
              this.status = response.Data.status as CourseStatusEnum;
            }
          }
        })
      );
    }
  }
  openDeleteCourseDialogBox(courseId: string) {
    this.dialog.open(DeleteDialogBoxComponent, {
      width: '360px',
      data: { courseId }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.deleteCoursesById(courseId)
      }
    });
  }

  deleteCoursesById(courseId: string) {
    this.subscription.add(
      this.courseService.deleteCourseById(courseId).subscribe({
        next: () => {
          this._toastr.success('Course deleted successfully');
          this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
        },
        error: () => {
          this._toastr.error('Failed to delete course');
        }
      })
    );
  }

  openUnpublishDialogBox() {
    this.dialog.open(UnpublishedCourseDialogBoxComponent, {
      width: '400px',
      data: { courseId: this.courseId }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.patchPublishedCourseByStatus()
      }
    });
  }

  patchPublishedCourseByStatus() {
    if (!this.classroomId) {
      this._toastr.error('Classroom ID is required');
      return;
    }
    this.isPublish = true;
    this.subscription.add(
      this.courseService.patchPublishedCourseByStatus(this.classroomId, this.courseId).pipe(
        tap(response => {
          if (response.Success) {
            this.getCourseDetailsById();
            const courseStatus = response.Data?.status;
            if (courseStatus === CourseStatusEnum.Published) {
              this._toastr.success('Your course has been published and is now available to students');
              this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
            } else if (courseStatus === CourseStatusEnum.Draft) {
              this._toastr.success('Your course was Published and is now moved to Draft.');
            }
          } else {
            this._toastr.error(response.Message);
          }
        }),
        finalize(() => {
          this.isPublish = false;
        }),
        catchError(() => {
          this.isPublish = false;
          return of(null);
        })
      ).subscribe()
    );
  }
  goToPrePage() {
    this.router.navigate(['/teacher-dashboard', this.classroomId, 'manual-course-creation', 'add-resources']);
  }
  goBackToPreviousStep() {
    this.router.navigate([
      'admin-student-platform/course-setup/resources'
    ]);
  }

	goBack(): void {
			this.router.navigate(['/admin-student-platform/admin-courses']);
	}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }


  getThumbnailSrc(): string {
    const thumb = this.coursePayload.course_thumbnail;

    if (!thumb) return '';

    if (thumb instanceof File) {
      return URL.createObjectURL(thumb);
    }

    return thumb;
  }
  downloadDocument(): void {
    const doc = this.coursePayload.course_document;

    if (!doc) return;

    if (doc instanceof File) {
      const url = URL.createObjectURL(doc);
      this.forceDownload(url, doc.name);
      return;
    }

    this.forceDownload(doc, 'course-document');
  }

  private forceDownload(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.click();
  }

}
